'use client';

import { useEffect, useState } from 'react';
import { LanguageCode, LanguageType, Locale } from 'shared/src/types/languages.type';
import { ArticleNarrator } from '@/components/articles/article-narrator';

interface BlogArticleClientWrapperProps {
  content: string;
  children: React.ReactNode;
  articleId: string;
  lang?: LanguageType;
  locale: Locale;
  title: string;
  description: string;
}

/**
 * The client shell around an article: the reading-progress line under the
 * top bar, the stats row with the reader's "Listen" control, the article,
 * and the way back up. The reader itself is components/articles/article-narrator.tsx.
 */
const BlogArticleClientWrapper = ({
  content,
  children,
  articleId,
  lang = LanguageCode.english,
  locale,
  title,
  description,
}: BlogArticleClientWrapperProps) => {
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    const updateReadingProgress = () => {
      const article = document.querySelector('article');
      if (!article) return;
      const scrolled = window.scrollY - article.offsetTop;
      const progress = Math.min(Math.max(scrolled / article.offsetHeight, 0), 1);
      setReadingProgress(progress * 100);
    };
    window.addEventListener('scroll', updateReadingProgress, { passive: true });
    return () => window.removeEventListener('scroll', updateReadingProgress);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const wordCount = content.split(' ').length;
  const readingTime = Math.ceil(wordCount / 200);

  return (
    <>
      {/* Reading progress indicator */}
      <div className="fixed top-12 left-0 w-full h-1 z-50">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-green-600 transition-all duration-300"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <div className="relative">
        <div className="max-w-4xl mx-auto sm:pl-16 sm:px-8 lg:pl-8 lg:px-12">
          <div className="flex items-center justify-between mt-12 max-sm:flex-col max-sm:items-start max-sm:gap-4">
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
                {readingTime} min read
              </span>
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {wordCount.toLocaleString()} words
              </span>
            </div>

            <div className="flex items-center gap-2 max-sm:w-full">
              <ArticleNarrator articleId={articleId} lang={lang} locale={locale} title={title} description={description} />
            </div>
          </div>
        </div>

        {children}

        {/* Back to top button at the bottom */}
        <div className="max-w-4xl mx-auto sm:pl-16 sm:px-8 lg:pl-8 lg:px-12 mt-8 mb-12">
          <div className="flex justify-center">
            <button
              onClick={scrollToTop}
              className="flex items-center px-6 py-3 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Back to top
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogArticleClientWrapper;
